import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { DealsService } from '@services/requests/housien-services/DealsService';
import { useDealStageLabels } from '@features/deals/constants/deal-stages';

/** Stages an attendance can no longer receive a visit under. */
const SETTLED_STAGES = new Set(['won', 'lost']);

export interface AttendanceOption {
  value: string;
  label: string;
}

export interface ClientAttendances {
  isLoading: boolean;
  options: AttendanceOption[];
}

/**
 * The open attendances a visit can be booked under, for one client.
 *
 * An attendance has no name, so it is identified by what it is about --
 * the property, or the day it opened when there is none yet -- plus the
 * stage, which is what tells two attendances for the same client apart.
 *
 * @param agencyId - The owning agency.
 * @param clientId - The client the visit is for; empty disables the query.
 * @returns The view-model: a loading flag and the selectable options.
 */
export function useClientAttendances(agencyId: string, clientId: string): ClientAttendances {
  const { t } = useTranslation('features/visits');
  const stageLabels = useDealStageLabels();

  const query = useQuery({
    queryKey: ['visits', agencyId, 'attendances', clientId] as const,
    queryFn: () => DealsService.listDeals(agencyId, 1, 50, undefined, clientId),
    enabled: Boolean(agencyId && clientId),
  });

  const options = useMemo<AttendanceOption[]>(() => {
    const rows = query.data?.data ?? [];
    return rows
      .filter((deal) => !SETTLED_STAGES.has(deal.stage))
      .map((deal) => ({
        value: deal.deal_id,
        // Stage first: it is what tells two attendances for the same
        // client apart, and property titles here run long enough to
        // truncate everything after them.
        label: `${stageLabels[deal.stage as keyof typeof stageLabels]} · ${describe(deal, t)}`,
      }));
  }, [query.data, stageLabels, t]);

  return { isLoading: query.isLoading, options };
}

/**
 * What an attendance is about, in one phrase.
 *
 * @param deal - The attendance row from the list endpoint.
 * @param t - The visits-namespace translator.
 * @returns The property it concerns, or the day it opened.
 */
function describe(
  deal: { property_title?: string | null; created_at?: string | null },
  t: (key: string, params?: Record<string, string>) => string,
): string {
  if (deal.property_title) return deal.property_title;
  if (deal.created_at) {
    return t('drawer.attendanceOpenedOn', {
      date: new Date(deal.created_at).toLocaleDateString(),
    });
  }
  return t('drawer.attendanceUntitled');
}
